/**
 * Page-level review summary bar for the Quiz Editor.
 *
 * Sticky-feeling header above the question list. Surfaces three counts and a
 * single-click "Approve all pending" action. Auto-collapses into a neutral
 * "all approved" green banner once nothing is pending.
 *
 * Extracted verbatim from QuizEditorPage.jsx during the Phase 3 refactor
 * (no behaviour change).
 */
import { useMemo } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { effectiveReviewStatus } from './quizMeta';

export function ReviewSummaryBar({ questions, onApproveAll, busy }) {
  const counts = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0;
    const pendingIds = [];
    for (const q of questions) {
      const s = effectiveReviewStatus(q);
      if (s === 'pending') { pending += 1; pendingIds.push(q.question_id); }
      else if (s === 'rejected') rejected += 1;
      else approved += 1;
    }
    return { pending, approved, rejected, pendingIds };
  }, [questions]);

  if (counts.pending === 0 && counts.rejected === 0) {
    return (
      <div
        className="rounded-lg border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-900/60 px-4 py-2.5 flex items-center gap-2.5 text-sm"
        data-testid="review-summary-all-clear"
      >
        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
        <span className="text-emerald-800 dark:text-emerald-300 font-medium">
          All {counts.approved} question{counts.approved === 1 ? '' : 's'} approved
        </span>
        <span className="text-emerald-700/70 dark:text-emerald-400/70 text-xs">
          · ready for live use
        </span>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border bg-card px-4 py-2.5 flex items-center gap-3 flex-wrap"
      data-testid="review-summary-bar"
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        {counts.pending > 0 && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60"
            data-testid="summary-pending-count"
          >
            <AlertTriangle className="w-3 h-3" /> {counts.pending} pending
          </span>
        )}
        {counts.approved > 0 && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60"
            data-testid="summary-approved-count"
          >
            <CheckCircle2 className="w-3 h-3" /> {counts.approved} approved
          </span>
        )}
        {counts.rejected > 0 && (
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-900/60"
            data-testid="summary-rejected-count"
          >
            <XCircle className="w-3 h-3" /> {counts.rejected} rejected
          </span>
        )}
      </div>
      <div className="flex-1" />
      {counts.pending > 0 && (
        <Button
          size="sm"
          onClick={() => onApproveAll(counts.pendingIds)}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="approve-all-pending-btn"
        >
          {busy
            ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
          Approve all pending ({counts.pending})
        </Button>
      )}
    </div>
  );
}
